import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DAYS, GBP, ukDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Upload } from "lucide-react";
import { SpecialDates, BrandingSettings, PromotionSettings, DepositSettings } from "@/components/BusinessExtrasSettings";
import { BillingPanel } from "@/components/BillingPanel";
import { StripeConnectCard } from "@/components/StripeConnectCard";
import { EmailPolicySettings } from "@/components/EmailPolicySettings";
import { DeleteAccountDialog } from "@/components/DeleteAccountDialog";

function DangerZone({ business, impersonating }) {
  const [open, setOpen] = useState(false);
  if (impersonating) return <p className="mt-5 text-xs text-stone-500" data-testid="danger-zone-impersonating">To delete this company, use Super admin → Companies.</p>;
  return (
    <div className="mt-5 bg-white rounded-2xl border border-rose-200 p-5 sm:p-6 shadow-sm" data-testid="danger-zone">
      <h2 className="font-semibold text-lg text-rose-700">Delete my account</h2>
      <p className="text-sm text-stone-600 mt-1">Permanently delete {business.name}, your booking page and all your data.</p>
      <Button variant="outline" data-testid="delete-my-account" onClick={() => setOpen(true)} className="mt-4 rounded-full text-rose-700 border-rose-300 hover:bg-rose-50">Delete account</Button>
      {open && <DeleteAccountDialog title="Delete your account?" confirmName={business.name} needPassword
        warning={`This permanently deletes ${business.name} and everything in it. Your booking page will stop working and clients can no longer book.`}
        items={["All bookings, clients and consent forms", "Services, staff logins, block-outs and vouchers", "Reviews, photos and settings"]}
        onClose={() => setOpen(false)}
        onConfirm={async (v) => {
          try { await api.post("/business/delete-account", v); }
          catch (e) { throw new Error(formatApiError(e.response?.data?.detail)); }
          window.location.href = "/";
        }} />}
    </div>
  );
}

export default function Settings() {
  const { business, loadBusiness, user } = useAuth();
  const [form, setForm] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [newRoom, setNewRoom] = useState("");
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => { if (business) setForm({ ...business }); }, [business]);
  const loadRooms = useCallback(async () => { const { data } = await api.get("/rooms"); setRooms(data); }, []);
  useEffect(() => { loadRooms(); }, [loadRooms]);

  if (!form) return <div className="text-stone-500">Loading…</div>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setHour = (d, k, v) => setForm((f) => ({ ...f, opening_hours: { ...f.opening_hours, [d]: { ...f.opening_hours[d], [k]: v } } }));
  const eveOf = (d) => form.opening_hours[d].evening || { on: false, start: "18:00", end: "21:00" };
  const setEve = (d, k, v) => setHour(d, "evening", { ...eveOf(d), [k]: v });
  const bookingUrl = `${window.location.origin}/${business.slug}`;

  const saveProfile = async () => {
    try {
      await api.put("/business", { name: form.name, owner_name: form.owner_name, phone: form.phone, address: form.address, business_type: form.business_type, slug: form.slug });
      if (logoFile) { const fd = new FormData(); fd.append("file", logoFile); await api.post("/business/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setLogoFile(null); }
      await loadBusiness();
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveHours = async () => {
    try { await api.put("/business", { opening_hours: form.opening_hours, staff_hours_sync: !!form.staff_hours_sync }); await loadBusiness(); toast.success(form.staff_hours_sync ? "Hours saved for the salon and all staff" : "Opening hours saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addRoom = async () => { if (!newRoom) return; await api.post("/rooms", { name: newRoom }); setNewRoom(""); loadRooms(); toast.success("Room added"); };
  const delRoom = async (id) => { await api.delete(`/rooms/${id}`); loadRooms(); };

  return (
    <div className="max-w-4xl">
      <h1 className="font-extrabold text-3xl">Settings</h1>
      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "profile"} className="mt-5">
        <TabsList className="rounded-full flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="settings-tab-profile" className="rounded-full">Business</TabsTrigger>
          <TabsTrigger value="hours" data-testid="settings-tab-hours" className="rounded-full">Hours</TabsTrigger>
          <TabsTrigger value="rooms" data-testid="settings-tab-rooms" className="rounded-full">Rooms</TabsTrigger>
          <TabsTrigger value="branding" data-testid="settings-tab-branding" className="rounded-full">Branding</TabsTrigger>
          <TabsTrigger value="promotions" data-testid="settings-tab-promotions" className="rounded-full">Promotions</TabsTrigger>
          <TabsTrigger value="subscription" data-testid="settings-tab-subscription" className="rounded-full">Subscription</TabsTrigger>
          <TabsTrigger value="payments" data-testid="settings-tab-payments" className="rounded-full">Payments</TabsTrigger>
          <TabsTrigger value="emails" data-testid="settings-tab-emails" className="rounded-full">Emails & policy</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-5">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl border border-stone-200 bg-stone-50 overflow-hidden flex items-center justify-center">
                {(logoFile || business.logo_url) ? <img src={logoFile ? URL.createObjectURL(logoFile) : `${process.env.REACT_APP_BACKEND_URL}${business.logo_url}`} alt="" className="h-full w-full object-cover" /> : <Upload className="h-5 w-5 text-stone-400" />}
              </div>
              <label className="cursor-pointer"><input type="file" accept="image/*" data-testid="settings-logo-input" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0])} />
                <span className="inline-flex items-center px-4 py-2 rounded-full border border-stone-300 text-sm hover:bg-stone-50">Change logo</span></label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Business name</Label><Input data-testid="settings-name" value={form.name} onChange={set("name")} className="mt-1.5 h-10 rounded-lg" /></div>
              <div><Label>Owner name</Label><Input value={form.owner_name} onChange={set("owner_name")} className="mt-1.5 h-10 rounded-lg" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5 h-10 rounded-lg" /></div>
              <div><Label>Type</Label><Input value={form.business_type} onChange={set("business_type")} className="mt-1.5 h-10 rounded-lg" /></div>
            </div>
            <div><Label>Address</Label><Textarea value={form.address} onChange={set("address")} className="mt-1.5 rounded-lg" rows={2} /></div>
            <div>
              <Label>Booking URL slug</Label>
              <div className="mt-1.5 flex items-center rounded-lg border border-stone-300 overflow-hidden h-10">
                <span className="px-3 text-sm text-stone-500 bg-stone-50 h-full flex items-center border-r border-stone-200">getbookedin.co.uk/</span>
                <input data-testid="settings-slug" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))} className="flex-1 px-3 h-full outline-none text-sm font-mono" />
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className="text-stone-500 truncate">{bookingUrl}</span>
                <button data-testid="settings-copy-url" onClick={() => { navigator.clipboard?.writeText(bookingUrl); toast.success("Copied"); }} className="text-[#B07E4F]"><Copy className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="flex justify-end"><Button data-testid="save-settings-button" onClick={saveProfile} className="rounded-full bg-stone-900 hover:bg-stone-800">Save changes</Button></div>
          </div>
          <DangerZone business={business} impersonating={!!user?.impersonated_by} />
        </TabsContent>

        <TabsContent value="hours" className="mt-5">
          <div className="bg-white rounded-2xl border border-stone-200 p-5 sm:p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-4 pb-4 border-b border-stone-100">
              <div><div className="font-medium text-sm">Same hours for all staff</div>
                <p className="text-xs text-stone-500 mt-0.5">On: these hours are everyone's working hours, and a one-person salon's own hours update these. Off: each person sets their own hours in Staff.</p></div>
              <Switch data-testid="settings-staff-hours-sync" checked={!!form.staff_hours_sync} onCheckedChange={(v) => setForm((f) => ({ ...f, staff_hours_sync: v }))} />
            </div>
            <p className="text-sm text-stone-500 mb-3">Day hours on the left, optional evening hours on the right. Evenings fill in order, with no gaps.</p>
            <div className="space-y-2">
              {DAYS.map((day, d) => (
                <div key={d} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-1.5">
                  <div className="w-24 text-sm font-medium">{day}</div>
                  <Switch checked={!form.opening_hours[d].closed} onCheckedChange={(v) => setHour(d, "closed", !v)} data-testid={`settings-day-${d}`} />
                  {form.opening_hours[d].closed ? <span className="text-sm text-stone-400">Closed</span> : (
                    <div className="flex items-center gap-2">
                      <input type="time" value={form.opening_hours[d].open} onChange={(e) => setHour(d, "open", e.target.value)} className="border border-stone-300 rounded-lg px-2 py-1 text-sm" />
                      <span className="text-stone-400">–</span>
                      <input type="time" value={form.opening_hours[d].close} onChange={(e) => setHour(d, "close", e.target.value)} className="border border-stone-300 rounded-lg px-2 py-1 text-sm" />
                    </div>
                  )}
                  {!form.opening_hours[d].closed && (
                    <div className="flex flex-wrap items-center gap-2 pl-0 sm:pl-3 sm:ml-1 sm:border-l border-stone-200 basis-full sm:basis-auto" data-testid={`settings-evening-${d}`}>
                      <Switch checked={!!eveOf(d).on} onCheckedChange={(v) => setEve(d, "on", v)} data-testid={`settings-evening-toggle-${d}`} />
                      <span className="text-xs text-stone-500">Evening</span>
                      {eveOf(d).on && (
                        <>
                          <input type="time" data-testid={`settings-evening-start-${d}`} value={eveOf(d).start} onChange={(e) => setEve(d, "start", e.target.value)} className="border border-stone-300 rounded-lg px-2 py-1 text-sm" />
                          <span className="text-stone-400">–</span>
                          <input type="time" data-testid={`settings-evening-end-${d}`} value={eveOf(d).end} onChange={(e) => setEve(d, "end", e.target.value)} className="border border-stone-300 rounded-lg px-2 py-1 text-sm" />
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4"><Button data-testid="save-hours-button" onClick={saveHours} className="rounded-full bg-stone-900 hover:bg-stone-800">Save hours</Button></div>
          </div>
          <SpecialDates business={business} onSaved={loadBusiness} />
        </TabsContent>

        <TabsContent value="branding" className="mt-5"><BrandingSettings business={business} onSaved={loadBusiness} /></TabsContent>
        <TabsContent value="promotions" className="mt-5"><PromotionSettings business={business} onSaved={loadBusiness} /></TabsContent>

        <TabsContent value="rooms" className="mt-5">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <p className="text-sm text-stone-600">Rooms/resources prevent double-booking when a service needs a specific space or equipment.</p>
            <div className="mt-4 flex gap-2">
              <Input data-testid="room-name-input" value={newRoom} onChange={(e) => setNewRoom(e.target.value)} placeholder="e.g. Treatment Room 1" className="h-10 rounded-lg" />
              <Button data-testid="add-room-button" onClick={addRoom} className="rounded-full bg-stone-900 hover:bg-stone-800"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="mt-4 space-y-2">
              {rooms.map((r) => (
                <div key={r.id} className="flex items-center justify-between border border-stone-100 rounded-lg px-4 py-2.5">
                  <span className="text-sm font-medium">{r.name}</span>
                  <button data-testid={`delete-room-${r.id}`} onClick={() => delRoom(r.id)} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {rooms.length === 0 && <p className="text-sm text-stone-400">No rooms yet.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="subscription" className="mt-5">
          <BillingPanel />
        </TabsContent>

        <TabsContent value="payments" className="mt-5">
          <div className="space-y-4">
            <DepositSettings business={business} onSaved={loadBusiness} />
            <StripeConnectCard />

            <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-stone-900 flex items-center justify-center text-white font-bold">□</div>
                  <div><h2 className="font-semibold text-lg leading-tight">Square</h2><p className="text-sm text-stone-500">Connect your own Square account</p></div>
                </div>
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-stone-100 text-stone-600">Not connected</span>
              </div>
              <div className="mt-4 text-sm text-stone-600">
                <p>Square payouts are coming soon. For now, connect payments with Stripe above.</p>
                <Button disabled data-testid="connect-square-button" className="mt-4 rounded-full bg-stone-900 hover:bg-stone-800 opacity-60 cursor-not-allowed">Connect Square (coming soon)</Button>
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="emails" className="mt-5"><EmailPolicySettings business={business} onSaved={loadBusiness} /></TabsContent>
      </Tabs>
    </div>
  );
}
